import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Eye, EyeOff, AlertCircle } from 'lucide-react';
import SocialAuthButtons from '../components/SocialAuthButtons';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, complete2FALogin } = useAuth();
  const { toast } = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  // 2FA challenge state — set when the password step returns requires2FA.
  // While `twoFA.userId` is truthy we show the code-input view instead of
  // the password form.
  const [twoFA, setTwoFA] = useState({ userId: null, code: '' });

  // Honour ?email= and ?next= query params. Set by the dup-signup modal
  // and the AddRolePage redirect — pre-fills the email field and (after
  // login) routes the user to the requested path instead of the default
  // role-aware destination. Validated to be an in-app path to avoid
  // open-redirect.
  const params = new URLSearchParams(location.search);
  const nextRaw = params.get('next') || '';
  const nextSafe = nextRaw.startsWith('/') && !nextRaw.startsWith('//') ? nextRaw : null;

  useEffect(() => {
    const prefill = params.get('email');
    if (prefill && !formData.email) {
      setFormData(f => ({ ...f, email: prefill }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Redirect once tokens are in hand. Shared by the password-only path and
  // the 2FA second step. ?next= wins over the role-aware default.
  const redirectAfterLogin = (user) => {
    if (nextSafe) {
      navigate(nextSafe, { replace: true });
      return;
    }
    const roles = user.roles || [];
    if (roles.includes('admin'))                                                 navigate('/admin/dashboard', { replace: true });
    else if (roles.includes('landlord')         && user.activeMode === 'landlord')         navigate('/landlord/dashboard', { replace: true });
    else if (roles.includes('service_provider') && user.activeMode === 'service_provider') navigate('/provider/dashboard', { replace: true });
    else if (roles.includes('host')             && user.activeMode === 'host')             navigate('/host/dashboard', { replace: true });
    else                                                                                    navigate('/guest/browse', { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await login(formData.email, formData.password);

      // Server says: "password OK, now prove it's you with TOTP".
      if (result?.requires2FA) {
        setTwoFA({ userId: result.userId, code: '' });
        return;
      }

      // Admin without 2FA enrolled — server refuses to issue a session
      // until enrolment is complete. Surface to the user; enrolment runs
      // out of band via a recovery channel (see backend comment).
      if (result?.requires2FASetup) {
        setError(
          result.message ||
          'Two-factor authentication is required for admin accounts. Please complete enrolment before signing in.'
        );
        return;
      }

      toast({ title: 'Welcome back!', description: `Logged in as ${result.user.name}` });
      redirectAfterLogin(result.user);
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.response?.data?.detail || 'Invalid email or password';
      setError(errorMessage);
      toast({ title: 'Login Failed', description: errorMessage, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    if (!twoFA.userId) return;
    const code = (twoFA.code || '').trim();
    if (code.length < 6) {
      setError('Enter the 6-digit code from your authenticator app (or a backup code).');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const result = await complete2FALogin(twoFA.userId, code);
      toast({ title: 'Welcome back!', description: `Logged in as ${result.user.name}` });
      redirectAfterLogin(result.user);
    } catch (err) {
      console.error('2FA login error:', err);
      const msg = err.response?.data?.detail || 'Invalid code. Please try again.';
      setError(msg);
      toast({ title: 'Verification failed', description: msg, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const cancel2FA = () => {
    setTwoFA({ userId: null, code: '' });
    setError('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F4F2E9] to-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/">
            <img src="/logo.png" alt="Ariv" className="h-[38px] w-auto object-contain mx-auto mb-4" />
          </Link>
          <h1 className="text-3xl font-bold text-[#1F3A68]">Welcome Back</h1>
          <p className="text-gray-600 mt-2">Log in to manage your properties or bookings</p>
        </div>

        <Card className="border-2 border-[#1F3A68]/10 shadow-xl">
          <CardHeader>
            <CardTitle className="text-2xl text-center text-[#1F3A68]">
              {twoFA.userId ? 'Verify it’s you' : 'Log In'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
                <AlertCircle size={18} />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {twoFA.userId ? (
              <form onSubmit={handle2FASubmit} className="space-y-4">
                <p className="text-sm text-gray-600">
                  Enter the 6-digit code from your authenticator app. If you have lost access to it, use one of the
                  backup codes you saved when enabling 2FA.
                </p>
                <div>
                  <Label htmlFor="twofa-code" className="text-base font-semibold">Authentication code</Label>
                  <Input
                    id="twofa-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="000000"
                    value={twoFA.code}
                    onChange={(e) => setTwoFA(t => ({ ...t, code: e.target.value.replace(/\s/g, '').slice(0, 10) }))}
                    className="mt-1 h-12 text-center text-lg tracking-[0.5em] font-mono"
                    maxLength={10}
                    autoFocus
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-[#1F3A68] hover:bg-[#1F3A68]/90 text-white h-12 text-lg"
                  disabled={loading || (twoFA.code || '').trim().length < 6}
                >
                  {loading ? 'Verifying…' : 'Verify and continue'}
                </Button>
                <button
                  type="button"
                  onClick={cancel2FA}
                  className="w-full text-sm text-gray-500 hover:text-gray-700"
                >
                  Use a different account
                </button>
              </form>
            ) : (
            <>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-base font-semibold">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="mt-1 h-12"
                  data-testid="login-email"
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-base font-semibold">Password</Label>
                <div className="relative mt-1">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    required
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="h-12 pr-10"
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 rounded border-gray-300" />
                  <span className="text-sm text-gray-600">Remember me</span>
                </label>
                <Link to="/forgot-password" className="text-sm text-[#1F3A68] hover:underline">
                  Forgot password?
                </Link>
              </div>

              <Button
                type="submit"
                className="w-full bg-[#1F3A68] hover:bg-[#1F3A68]/90 text-white h-12 text-lg"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? 'Logging in...' : 'Log In'}
              </Button>
            </form>

            <SocialAuthButtons
              mode="login"
              onSuccess={(result) => {
                if (result?.requires2FA) {
                  // Switch to the in-page 2FA challenge.
                  setTwoFA({ userId: result.userId, code: '' });
                  return;
                }
                if (result?.requires2FASetup) {
                  setError(
                    result.message ||
                    'Two-factor authentication is required for admin accounts. Please complete enrolment before signing in.'
                  );
                  return;
                }
                redirectAfterLogin(result.user);
              }}
            />

            <div className="mt-6 text-center">
              <p className="text-gray-600">
                Don't have an account?{' '}
                <Link to="/signup" className="text-[#1F3A68] font-semibold hover:underline">
                  Sign up
                </Link>
              </p>
            </div>
            </>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-sm text-gray-500 mt-6">
          By continuing, you agree to our{' '}
          <Link to="/terms" className="underline">Terms of Service</Link> and{' '}
          <Link to="/privacy" className="underline">Privacy Policy</Link>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
